import { createTrellusHostClient, type TrellusClientEvent, type TrellusHostClientOptions } from './host-client';
import { setActiveTrellusClient } from './host-runtime';
import {
  applyTrellusBulkDialResult,
  applyTrellusCapabilities,
  applyTrellusCompletionPolicy,
  applyTrellusContext,
  applyTrellusFocusResult,
  applyTrellusGongConnectionState,
  applyTrellusSelectionResult,
  applyTrellusTaskEvent,
  applyTrellusUiConfigurationChanged,
  resetTrellusHost,
  setTrellusHandshake,
} from './trellus-host-store';
import { applyTrellusSidebarState, refreshTrellusSidebarState, resetTrellusSidebar } from './trellus-sidebar-store';

/**
 * Wires one `TrellusHostClient` to the two stores. Page events are UI hints:
 * this file only ever writes LOCAL state (no network, no backend call), and
 * never dispatches anything back at Trellus in response to an event.
 */
export function routeTrellusEvent(event: TrellusClientEvent): void {
  switch (event.type) {
    case 'capabilities': applyTrellusCapabilities(event.capabilities); break;
    case 'context': applyTrellusContext(event.context); break;
    case 'selection_result': applyTrellusSelectionResult(event.result); break;
    case 'focus_result': applyTrellusFocusResult(event.result); break;
    case 'sidebar_state': applyTrellusSidebarState(event.state); break;
    case 'bulk_dial_result': applyTrellusBulkDialResult(event.result); break;
    case 'gong_connection_state': applyTrellusGongConnectionState(event.state); break;
    case 'ui_configuration_changed': applyTrellusUiConfigurationChanged(event.change.component_id, event.change.config_revision); break;
    case 'completion_policy_changed': applyTrellusCompletionPolicy(event.change); break;
    case 'task_event': applyTrellusTaskEvent(event.event); break;
  }
}

/**
 * Start the document's Trellus connection: create the client, publish it,
 * route its events into the stores, run the capability handshake. Returns the
 * teardown (dispose client, clear the active client, reset both stores).
 * Safe to call twice in a row after teardown (StrictMode setup→cleanup→setup):
 * each call builds fresh state and holds no module-level lifetime flag.
 */
export function startTrellusHost(options: TrellusHostClientOptions = {}): () => void {
  const client = createTrellusHostClient(options);
  setActiveTrellusClient(client);
  let stopped = false;
  let nonce: string | null = null;

  const handshake = async (): Promise<void> => {
    setTrellusHandshake('pending');
    const result = await client.connect();
    if (stopped) return;
    if (!result.ok) { setTrellusHandshake('unavailable'); return; }
    // `connect()` already routed the capabilities event; prime what depends on them.
    await refreshTrellusSidebarState();
    if (stopped || !client.getCapabilities()?.commands.includes('events.replay')) return;
    // Pick up events emitted before we subscribed (bounded to the last 100; ids dedupe).
    await client.replayEvents(0);
  };

  const unsubscribe = client.subscribe(event => {
    if (event.type === 'capabilities') {
      const next = event.capabilities?.nonce ?? null;
      // A new document identity restarts Trellus's sidebar sequence.
      if (nonce !== null && next !== nonce) resetTrellusSidebar();
      nonce = next ?? nonce;
      routeTrellusEvent(event);
      // Went stale (extension reloaded): look again, once; a timeout just leaves it "unavailable".
      if (event.capabilities === null && !stopped) void handshake();
      return;
    }
    routeTrellusEvent(event);
  });

  void handshake();

  return () => {
    stopped = true;
    unsubscribe();
    client.dispose();
    setActiveTrellusClient(null);
    resetTrellusHost();
    resetTrellusSidebar();
  };
}
